import { Banner, Card, Pill } from '../components/ui.js';
import { useAuth, useCan } from '../lib/auth.js';

/**
 * Operator runbook.
 *
 * Lives in the app rather than in a wiki because the steps reference specific
 * screens and the order matters -- and because the two things most likely to go
 * wrong (publishing without pushing, and local signatures colliding with the
 * transport rule) are invisible until somebody's mail is wrong.
 *
 * Gated on `user:manage`, which only ADMIN holds. Widen that check if you want
 * marketing or IT to read their own half.
 */
export function RunbookPage(): JSX.Element {
  const can = useCan();
  const { config } = useAuth();

  if (!can('user:manage')) {
    return (
      <Banner tone="info">
        <strong>Administrators only</strong>
        <div className="tiny">
          This page documents the full deployment process, including steps your role cannot
          perform. Ask an administrator if you need it.
        </div>
      </Banner>
    );
  }

  return (
    <div className="col" style={{ gap: 14, maxWidth: 900 }}>
      <header className="page-head">
        <div>
          <h1>Runbook</h1>
          <p className="sub">
            How a signature gets from an idea to somebody&apos;s mailbox. Read the two warnings
            first — they are the mistakes that actually happen.
          </p>
        </div>
      </header>

      <Banner tone="warn">
        <strong>Publishing is not deploying</strong>
        <div className="tiny">
          Publishing marks a version as the current approved one inside this tool. Nothing reaches
          a mailbox until you also run a <strong>deployment</strong>. Almost every &ldquo;my change
          didn&apos;t work&rdquo; report is a published version that was never pushed.
        </div>
      </Banner>

      <Banner tone="warn">
        <strong>Clear local Outlook signatures before you target anyone</strong>
        <div className="tiny">
          The transport rule appends server-side and cannot see what is already in the message. If
          somebody has their own signature in Outlook, they get two. Clear them via Intune or
          Group Policy, and add people to the target group only once theirs is gone.
        </div>
      </Banner>

      {config.features.globalDryRun && (
        <Banner tone="info">
          <strong>This environment is in dry-run mode</strong>
          <div className="tiny">
            Deployments report what they would do without changing Exchange. Untick{' '}
            <em>Dry run</em> on an individual deployment to make it real.
          </div>
        </Banner>
      )}

      <Card title="1 — Build a template">
        <Steps
          items={[
            <>
              <strong>Templates → New</strong>, or open an existing one. Use{' '}
              <strong>Import</strong> to bring in signature HTML you already have — paste it or
              choose a <code>.htm</code> file from <code>%APPDATA%\Microsoft\Signatures</code>.
            </>,
            <>
              When importing, use the <strong>&ldquo;whose signature is this?&rdquo;</strong>{' '}
              picker. It matches that person&apos;s real name, title, phone and email against the
              directory and replaces them with merge fields. Skip it and you ship one
              person&apos;s details to everybody.
            </>,
            <>
              Add blocks in the <strong>Design</strong> tab. Merge fields like{' '}
              <code>{'{{DisplayName}}'}</code> and <code>{'{{JobTitle}}'}</code> are filled per
              person at send time.
            </>,
            <>
              Watch <strong>Rendering issues</strong>. Errors block submission; warnings do not.
              Click <strong>Run full check</strong> to download and inspect the image files too —
              that is the pass that catches oversized logos and unreachable URLs.
            </>,
          ]}
        />
      </Card>

      <Card title="2 — Check it renders">
        <Steps
          items={[
            <>
              <strong>Preview</strong> tab. Compare Outlook desktop, OWA and Gmail side by side —
              those three behave most differently.
            </>,
            <>
              Tick <strong>Dark mode</strong>. Outlook for Windows and the Gmail app darken the
              surface but ignore a dark-mode image swap, so a transparent dark logo disappears
              there. A backing colour on the image block is the only fix that reaches every
              client.
            </>,
            <>
              Use <strong>Preview as</strong> to render for a real person, and check somebody with
              a blank job title or no phone number. Field blocks hide themselves when empty, but
              it is worth seeing.
            </>,
          ]}
        />
      </Card>

      <Card title="3 — Versions and approval">
        <p className="hint" style={{ marginTop: 0 }}>
          A version is frozen once published, so what is live cannot be edited underneath you. To
          change a live signature you restore it as a new draft.
        </p>
        <Flow
          steps={[
            { label: 'DRAFT', note: 'editable' },
            { label: 'IN REVIEW', note: 'Submit for review' },
            { label: 'APPROVED', note: 'Approve' },
            { label: 'PUBLISHED', note: 'Publish' },
          ]}
        />
        <Steps
          items={[
            <>
              <strong>Save as new version</strong> whenever you want a checkpoint. Add a change
              note — it is what the version list shows.
            </>,
            <>
              <strong>Submit for review</strong> → <strong>Approve</strong> →{' '}
              <strong>Publish</strong>. Only an <Pill tone="outline">ADMIN</Pill> can approve their
              own work, so a marketing author genuinely cannot self-publish. That is the
              two-person gate working.
            </>,
            <>
              To change what is live: <strong>Versions</strong> → find the published version →{' '}
              <strong>Restore</strong>. That copies it into a new draft. Nobody&apos;s mailbox
              changes until you deploy.
            </>,
            <>
              Publishing supersedes the previous live version, and the old one stays in the list
              so you can restore it.
            </>,
          ]}
        />
      </Card>

      <Card title="4 — Who gets which template">
        <Steps
          items={[
            <>
              <strong>Assignment rules</strong> map a template onto a population — all users, a
              security group, an OU, one person, or an attribute such as{' '}
              <code>department=Marketing</code>.
            </>,
            <>
              Check the <strong>coverage</strong> panel before you deploy. It shows who your rules
              leave with <em>no</em> template. A push that skips forty people otherwise reports as
              a clean success.
            </>,
            <>
              Assignment rules are for planning. The deployment screen takes its own target, so
              the two are set separately.
            </>,
          ]}
        />
      </Card>

      <Card title="5 — Deploy">
        <Steps
          items={[
            <>
              <strong>Sync the directory first</strong> (Administration → directory sync). Targets
              resolve against the local snapshot, so an unsynced group resolves to nobody and the
              run is refused.
            </>,
            <>
              <strong>New deployment</strong> → pick the template, version, mechanism and target.
              Choosing a single <strong>user</strong> marks the run as a test, which is the one
              case that can deploy an unpublished draft.
            </>,
            <>
              <strong>Run preflight.</strong> Read the issues. Errors stop the run; warnings tell
              you which merge fields Exchange cannot provide.
            </>,
            <>
              Leave <strong>Dry run</strong> ticked for a rehearsal — it logs the exact command it
              would issue and changes nothing. Untick it to apply.
            </>,
            <>
              <strong>Schedule for later</strong> arms it for a launch date.{' '}
              <strong>Temporary</strong> gives it an end date, and the scheduler rolls it back
              automatically — that is how a marketing banner comes off on its own.
            </>,
          ]}
        />
        <Banner tone="info">
          <strong>Order for a first rollout</strong>
          <div className="tiny">
            Dry run to one person → real push to that person → check the{' '}
            <em>recipient&apos;s</em> copy → dry run to the group and check the user count → real
            push to the group.
          </div>
        </Banner>
      </Card>

      <Card title="6 — Verify, and undo">
        <Steps
          items={[
            <>
              A transport-rule signature is <strong>not visible while composing</strong> and is
              absent from Sent Items. It is added in transit. Always check what the recipient
              received, ideally at an external address.
            </>,
            <>
              It also lands at the very bottom of a reply chain, below the quoted history. On a
              reply, the signature near your text is the one Outlook inserted locally — not this
              one.
            </>,
            <>
              Exchange can take up to 30 minutes to propagate a rule change. A message sent
              straight after a push may still carry the previous signature.
            </>,
            <>
              <strong>History</strong> → open the deployment. The button says what it will do:{' '}
              <strong>Remove signature</strong> if that push created the rule, or{' '}
              <strong>Restore previous version</strong> if it replaced an earlier one. When it says
              restore, a separate <strong>Remove signature</strong> button sits beside it — use that
              to take the signature off entirely rather than stepping back a version.
            </>,
            <>
              <strong>Retry</strong> re-runs only the failed rows, so a partial run does not have
              to be repeated in full.
            </>,
          ]}
        />
      </Card>

      <Card title="Roles">
        <table className="table">
          <thead>
            <tr>
              <th>Role</th>
              <th>Can</th>
              <th>Cannot</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['ADMIN', 'Everything, including roles and self-approval', '—'],
              ['MARKETING', 'Design, preview, submit for review', 'Approve, publish, deploy'],
              ['APPROVER', 'Approve or reject, publish', 'Author templates'],
              ['IT', 'Deploy, schedule, roll back', 'Edit template design'],
              ['VIEWER', 'Read everything', 'Change anything'],
            ].map(([role, yes, no]) => (
              <tr key={role}>
                <td>
                  <Pill tone="outline">{role}</Pill>
                </td>
                <td className="tiny">{yes}</td>
                <td className="tiny">{no}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="hint">
          To add somebody: assign them to the <strong>Email Signature Manager</strong> enterprise
          application in Entra (Users and groups → Add user), then have them sign in once. They
          are created as VIEWER and you promote them in Administration. They will not appear in
          the list until they have signed in.
        </p>
      </Card>

      <Card title="When something looks wrong">
        <table className="table">
          <thead>
            <tr>
              <th>Symptom</th>
              <th>Usually</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Change not showing', 'Published but never deployed. Check History for the version number that was actually pushed.'],
              ['Two signatures', 'The person has a local Outlook signature as well. Check both the new-message and reply/forward settings.'],
              ['Old signature on a reply', 'You are reading the copy quoted in the thread. Scroll to the very bottom.'],
              ['No signature at all', 'Check the deployment was not rolled back, and that the target still resolves to that person.'],
              ['Rollback gave the wrong signature', 'It reinstated the version that push replaced. Use Remove signature instead.'],
              ['A merge field came out blank', 'The attribute is empty in Entra. Field blocks hide themselves rather than leaving a stray label.'],
              ['A merge field shipped literally', 'That field has no Exchange equivalent. Preflight lists them; use the Intune provider if you need it.'],
              ['Logo looks soft or clipped', 'The source file is far larger than the display size. Re-export at twice the display width.'],
            ].map(([symptom, cause]) => (
              <tr key={symptom}>
                <td className="tiny cell-strong">{symptom}</td>
                <td className="tiny">{cause}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title="What is on the record">
        <p className="hint" style={{ marginTop: 0 }}>
          Every template change, approval, publish, deployment and rollback is written to the{' '}
          <strong>Audit trail</strong> with who, what and when. It is append-only and exportable
          as CSV. Deployments additionally keep a per-person row with the rendered signature and
          the previous one, which is what makes rollback exact.
        </p>
      </Card>
    </div>
  );
}

function Steps({ items }: { items: React.ReactNode[] }): JSX.Element {
  return (
    <ol className="runbook-steps">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ol>
  );
}

function Flow({ steps }: { steps: Array<{ label: string; note: string }> }): JSX.Element {
  return (
    <div className="row wrap" style={{ gap: 6, margin: '4px 0 12px' }}>
      {steps.map((s, i) => (
        <span key={s.label} className="row" style={{ gap: 6 }}>
          <span className="runbook-flow">
            <strong className="tiny">{s.label}</strong>
            <span className="tiny muted">{s.note}</span>
          </span>
          {i < steps.length - 1 && (
            <span className="tiny muted" aria-hidden="true">
              →
            </span>
          )}
        </span>
      ))}
    </div>
  );
}
